import * as React from "react";
import { useState, useRef } from "react";
import { ChartContainer } from "@mui/x-charts/ChartContainer";
import {
  AreaPlot,
  MarkPlot,
  areaElementClasses,
} from "@mui/x-charts/LineChart";
import { LineChart } from '@mui/x-charts/LineChart';
import Stack from '@mui/material/Stack';
import { ChartsXAxis, ChartsYAxis } from "@mui/x-charts";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { Box, Typography } from "@mui/material";
import { useMsal } from "@azure/msal-react";

interface UploadData {
  [year: string]: {
    [month: string]: {
      day: number;
      total_upload_count: number;
    }[];
  };
}

interface UploadAreaChartProps {
  data: UploadData;
  selectedDate: {
    year: string;
    month: string;
  };
}

function GradientDefs() {
  const { top, bottom, height } = useDrawingArea();
  const svgHeight = top + bottom + height;

  return (
    <defs>
      <linearGradient
        id="area-gradient"
        x1="0"
        x2="0"
        y1="0"
        y2={`${svgHeight}`}
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="20.2%" stopColor="#D7A833" stopOpacity="1" />
        <stop
          offset="85.93%"
          stopColor="rgba(244, 223, 3, 0)"
          stopOpacity="0"
        />
      </linearGradient>
    </defs>
  );
}

const UploadAreaChart: React.FC<UploadAreaChartProps> = ({
  data,
  selectedDate,
}) => {
  const [selected, setSelected] = useState(selectedDate);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(0);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  if (Object.keys(data).length === 0) return <div>No data available</div>;

  const { year, month } = selected;
  const rawMonthData = data[year]?.[month] ?? [];
  const sortedMonthData = [...rawMonthData].sort((a, b) => a.day - b.day);

  const uData = sortedMonthData.map((item) => item.total_upload_count);
  const xLabels = sortedMonthData.map((item) => item.day.toString());

  return (
    <Box sx={{position:'relative'}}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: "20px",
          p: "20px",
          pb: "0px",
        }}
      >
        <Typography
          sx={{
            p: 1,
            fontSize: "16px",
            fontWeight: "600",
            maxWidth: "50%",
            display: "inline-flex",
          }}
        >
          Uploaded Documents
        </Typography>
        <Box sx={{ p: 2, display: "flex", gap: 2 }}>
          <select
            value={year}
            onChange={(e) => {
              const newYear = e.target.value;
              const months = Object.keys(data[newYear] || {});
              setSelected({
                year: newYear,
                month: months.length > 0 ? months[0] : "",
              });
            }}
            style={{ padding: "0.5rem" }}
          >
            {Object.keys(data).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <select
            value={month}
            onChange={(e) =>
              setSelected((prev) => ({
                ...prev,
                month: e.target.value,
              }))
            }
            style={{ padding: "0.5rem" }}
          >
            {data[year] &&
              Object.keys(data[year]).map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
          </select>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          pr: "20px",
          width: "100%",
        }}
      >
      
         <Stack sx={{ width: '82%' }}>
          <LineChart
         
            height={300}
            series={[
              {
                data: uData,
                type: "line",
                label: "Uploads",
                area: true,
                stack: "total",
                color: "#D7A833",
                showMark: false,
              },
            ]}
            xAxis={[
              {
                scaleType: "point",
                data: xLabels,
                position: "bottom",
                label: "Day of Month",
              },
            ]}
            yAxis={[
              {
                position: "right",
                tickLabelStyle: { display: "none" },
              },
            ]}
            sx={{
              [`& .${areaElementClasses.root}`]: {
                fill: "url(#area-gradient)",
              },
              "& circle[data-index]": {
                r: 4,
                fill: "#D70040",
              },
              "& .MuiChartsAxis-tickLabel": {
                fill: "#000000 !important",
                fontSize: "14px",
              },
              "& .MuiChartsAxis-label": {
                fill: "#000000 !important",
                fontSize: "14px",
              },
              "& .MuiChartsLegend-root":{
                display: "none !important",
              }
            }}
          >
            <GradientDefs />
            <AreaPlot />
            <MarkPlot />
            <ChartsXAxis />
            <ChartsYAxis />
          </LineChart>
          </Stack>
        </Box>

        <Box
          sx={{
            p: 1,
            pl:'5px',
            pr:'5px',
            backgroundColor: "#FAF3C0",
            borderRadius: "10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100px",
            width: "120px",
            position: "absolute",
            top: "90px",
            right: "20px",
          }}
        >
          <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
            {uData.reduce((acc, val) => acc + val, 0)}
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            Total Uploads
          </Typography>
        </Box>
      </Box>
     
  );
};

export default UploadAreaChart;
